'use client'
import { Box, Button, Center, Flex, Stack, Text } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { sendGTMEvent } from '@next/third-parties/google'
import { useTranslations } from 'next-intl'

import { SectionErrorBoundary } from '@/components/error/boundary'
import { GTM_EVENTS } from '@/configs/dataTrackingEvent'
import { useSsrContext } from '@/contexts/ssr'
import {
  isAPACAndMEACountry,
  isCAUser,
  isKoreanRegion,
  isUserInRestrictedRegion,
  isUSUser,
} from '@/i18n/country'
import { getEventNameFromUrl } from '@/libs/segment/utils'
import { gotoCdcAppDownloadPage, gotoCdcWebSignupPage } from '@/utils'

import CoinListModal from '../../_components/Trade/PriceConvertor/CoinListModal'
import { useCoinContext } from '../../_contexts/CoinContext'
import FakeSelector from './FakeSelector'
import styles from './index.module.css'
import OnChainTips from './OnChainTips'
import PayWith from './PayWith'
import PriceConvertor from './PriceConvertor'
import TradingTips, { TradingTipsType } from './TradingTips'
import WelcomeBonusTips from './WelcomeBonusTips'

interface TradeProps {
  locale: string
}

function Trade({ locale }: TradeProps) {
  const { coin } = useCoinContext()
  const { countryCode } = useSsrContext()
  const [conListModalVisible, { open, close }] = useDisclosure(false)
  const tradable = coin.app_tradable || coin.exchange_tradable
  const t = useTranslations('tradeWidget')

  // Use app download link for Korean locale and tradable coins, otherwise signup page
  const tradeButtonClick = () => {
    sendGTMEvent({
      event: GTM_EVENTS.WebBuyButtonClicked,
      path: window.location.pathname,
      coin: coin.symbol,
      entry_channel: getEventNameFromUrl(window.location.pathname),
    })
    if (tradable && isKoreanRegion(locale, countryCode)) {
      gotoCdcAppDownloadPage()
      return
    }
    gotoCdcWebSignupPage()
  }
  const isRestricted = isUserInRestrictedRegion(coin.restricted_areas, countryCode, locale)
  const isAPACAndMEA = isAPACAndMEACountry(countryCode)
  const isCA = isCAUser(countryCode)
  const isUS = isUSUser(countryCode)
  const isSupportedOnchain = isAPACAndMEA || isCA || isUS

  const showOnchainTips = !tradable && !isRestricted && isSupportedOnchain

  return (
    <>
      <Stack className={styles.tradeContainer} gap={24}>
        <Flex h={48} align="center">
          <Flex flex={1}>
            <Box className={`${styles.activeTab} ${styles.tabItem}`} mr={12}>
              <Text variant="heading3">{t('buy')}</Text>
            </Box>
            <Box className={styles.tabItem} onClick={tradeButtonClick}>
              <Text variant="heading3" c="var(--content-secondary)">
                {t('sell')}
              </Text>
            </Box>
          </Flex>
          <FakeSelector
            bg="var(--bg-surface-secondary)"
            content={<Text variant="subtitle">{t('oneTime')}</Text>}
            onClick={gotoCdcWebSignupPage}
          />
        </Flex>
        <PriceConvertor openCoinListModal={open} />
        {tradable && <PayWith />}
        {!isRestricted && (
          <Button radius="md" disabled={!tradable} onClick={tradeButtonClick}>
            {t('buy')} {coin.symbol}
          </Button>
        )}
        {tradable && !isRestricted && (
          <WelcomeBonusTips locale={locale} countryCode={countryCode} />
        )}
        {isRestricted && (
          <TradingTips
            symbol={coin.symbol}
            type={TradingTipsType.Restricted}
            openCoinListModal={open}
          />
        )}
        {showOnchainTips && <OnChainTips symbol={coin.symbol} />}
        {conListModalVisible && <CoinListModal visible={conListModalVisible} onClose={close} />}
      </Stack>
      {!isRestricted && tradable && (
        <Center className={styles.responsiveBuyButtonContainer}>
          <Button onClick={tradeButtonClick} h={44} w="100%">
            {t('buy')} {coin.symbol}
          </Button>
        </Center>
      )}
      {showOnchainTips && <OnChainTips symbol={coin.symbol} isMobile />}
    </>
  )
}

export default SectionErrorBoundary.with(Trade)
